<script setup lang="ts">
  import { computed, inject, onBeforeMount, useCssModule, useSlots } from 'vue';

  import { t } from '../../locale';
  import Icon from '../Icon/Icon.vue';
  import { STEPPER_INJECTION_KEY } from '../Stepper/keys';

  export interface StepProps {
    /**
     * Indicates if the step is currently active.
     * This takes precedence over `completed` and `error`
     */
    active?: boolean;
    /**
     * Indicates if the step is completed
     */
    completed?: boolean;
    /**
     * Indicates if the step has an error
     * This takes precedence over `completed`
     */
    error?: boolean;
    /**
     * The text to display in the step.
     */
    text: string;
    /**
     * Indicates if the step is nested
     */
    nested?: boolean;
    /**
     * The text to display in the step circle
     */
    step?: string;
    /**
     * Indicates if the step is disabled
     */
    disabled?: boolean;
    /**
     * Indicates if the step is in a loading state
     */
    loading?: boolean;
  }

  const props = withDefaults(defineProps<StepProps>(), {
    active: false,
    completed: false,
    error: false,
    nested: false,
    step: undefined,
    disabled: false,
    loading: false,
  });

  const slots = useSlots();
  const classes = useCssModule();

  const injectedStepper = inject(STEPPER_INJECTION_KEY);

  const isHorizontal = computed(() => injectedStepper?.orientation === 'horizontal');

  const isLightThemed = computed(() => injectedStepper?.theme === 'light');

  const showResponsiveNav = injectedStepper?.showResponsiveNav;

  const iconName = computed(() => {
    if (props.loading) {
      return 'working';
    }
    return props.completed ? 'circle-check' : 'circle-info';
  });

  const currentStepText = computed(() =>
    t('ll.stepper.currentStep', { num: props.step, total: String(injectedStepper?.stepCount.value) }),
  );

  // OnBeforeMount ensures that steps and substeps are registered in the correct order
  onBeforeMount(() => {
    if (injectedStepper) {
      injectedStepper.registerStep(props.nested);
    }
  });
</script>

<template>
  <li
    class="stash-step relative flex w-full"
    data-test="stash-step"
    :class="[
      classes.step,
      {
        [classes['nested-step--active']]: props.nested && props.active,
        'last:w-auto': isHorizontal && !showResponsiveNav,
        [classes['stash-step--singular']]: showResponsiveNav,
        'relative w-full shrink-0 snap-start flex-col justify-center': showResponsiveNav,
      },
    ]"
  >
    <div
      v-if="!props.nested"
      class="stash-step__indicator flex flex-col items-center"
      :class="{ 'mr-3': !showResponsiveNav }"
      data-test="stash-step|indicator"
    >
      <div
        :class="[
          classes.circle,
          {
            [classes['circle--todo']]: !props.error && !props.active && !props.completed,
            [classes['circle--error']]: props.error && !props.active,
            [classes['circle--completed']]: props.completed && !props.active,
            [classes['circle--active']]: props.active,
            [classes['circle--loading']]: props.loading,
          },
        ]"
      >
        <Icon
          v-if="(props.completed || props.error || props.loading) && !props.active"
          :name="iconName"
          :class="{ 'fx-spin': props.loading }"
          size="dense"
        />
        <span v-else>{{ props.step }}</span>
      </div>
      <div
        class="my-1.5 w-px bg-ice-500"
        :class="[classes.line, { [classes['line--expanded']]: props.completed || props.active, hidden: isHorizontal }]"
      />
    </div>
    <!-- step title, line(s), and nested steps -->
    <div
      class="flex"
      :class="[
        {
          'w-full flex-row items-center': isHorizontal,
          'mt-1.5 justify-center': showResponsiveNav,
          'flex-col': !isHorizontal || showResponsiveNav,
        },
      ]"
    >
      <div
        class="stash-step__title"
        data-test="stash-step|title"
        :class="[
          classes.title,
          {
            'whitespace-nowrap': isHorizontal,
            'cursor-pointer': !props.active && (props.completed || props.error),
            'cursor-not-allowed': props.disabled,
            'text-ice-900': isLightThemed,
            '!font-semibold': props.active,
            'text-white': !props.disabled && !isLightThemed,
            'place-self-center': showResponsiveNav,
          },
        ]"
      >
        {{ props.text }}
        <p
          v-if="showResponsiveNav"
          class="stash-step__pagination-label mb-0 text-center text-xs"
          data-test="stash-step|pagination-label"
          :class="isLightThemed ? 'text-ice-700' : 'text-white'"
        >
          {{ currentStepText }}
        </p>
      </div>
      <div
        v-if="isHorizontal"
        :class="[
          classes.line,
          {
            [classes['line--expanded']]: props.completed || props.active,
            'mx-1.5 border-t group-last:hidden': !showResponsiveNav,
          },
        ]"
      />
      <div class="stash-step--nested-steps" :class="{ 'pb-9': !nested && !slots.default, hidden: isHorizontal }">
        <ul :class="[classes.nested, { hidden: !slots.default }]">
          <!-- @slot Default slot to compose multiple nested steps -->
          <slot></slot>
        </ul>
      </div>
    </div>
  </li>
</template>

<style module>
  @reference "../../../styles/main.css";

  .step::before {
    content: '●';
    position: absolute;
    color: var(--color-white);
    left: -28px;
    opacity: 0;
    transition: opacity 500ms cubic-bezier(0.85, 0, 0.15, 1);
  }

  .title {
    font-weight: var(--stash-font-weight-medium);
    font-size: 1rem;
    margin-top: 1px;
    user-select: none;
  }

  .nested-step--active::before {
    opacity: 1;
  }

  .line {
    flex-grow: 0;
    transition: flex-grow 500ms cubic-bezier(0.87, 0, 0.13, 1);
  }

  .line--expanded {
    flex-grow: 1;
  }

  .stash-step--singular .line {
    @apply absolute
    inset-x-0
    top-3
    h-px
    border-t-0
    before:absolute
    before:left-0
    before:right-2/4
    before:mr-16
    before:block
    before:h-px
    before:bg-ice-500
    after:absolute
    after:left-2/4
    after:right-0
    after:ml-16
    after:block
    after:h-px
    after:bg-ice-500;
  }

  .stash-step--singular .line::after,
  .stash-step--singular .line::before {
    content: '';
  }

  .step:not(.stash-step--singular):last-child .line {
    display: none;
  }

  .circle {
    width: --spacing(6);
    height: --spacing(6);
    line-height: --spacing(6);
    border-radius: calc(infinity * 1px);
    display: grid;
    place-items: center;
    font-weight: var(--stash-font-weight-medium);
    transition:
      background-color 500ms,
      color 500ms;
  }

  .circle--todo {
    background-color: rgb(255 255 255 / 50%);
    color: #4d4a69;
    border: 1px solid #747293;
  }

  .circle--active {
    background-color: var(--color-blue-500);
    color: var(--color-white);
    border: 1px solid transparent;
  }

  .circle--loading {
    background-color: var(--color-white);
    border: 1px solid var(--color-ice-500);
    color: var(--color-ice-700);
    cursor: pointer;
  }

  .circle--completed {
    background-color: var(--color-white);
    border: 1px solid var(--color-ice-500);
    color: var(--color-green-500);
    cursor: pointer;
  }

  .circle--error {
    background-color: var(--color-white);
    border: 1px solid var(--color-ice-500);
    color: var(--color-red-500);
    cursor: pointer;
  }

  .nested {
    padding: calc(--spacing(3) + --spacing(1.5)) 0;
  }

  .nested li + li {
    margin-top: --spacing(3);
  }

  .nested .title {
    font-weight: var(--stash-font-weight-normal);
    font-size: 0.9rem;
  }
</style>
